import { useEffect, useState } from 'react';
import { ArrowLeft, Download, Pause, Play, SkipBack, SkipForward } from 'lucide-react';
import { Link } from 'react-router';
import {
  API_PREFIX,
  compareDates,
  formatAccuracy,
  formatCalendarDate,
  formatDateTime,
  formatDistance,
  formatSpeed,
  ukDate,
  type Device,
  type Location,
  type LocationHistoryResponse,
} from '@findmy/shared';
import { sourceLabel } from '../lib/device';
import { PRESETS, earliestDay, presetRange, rangeLabel, sameRange, type DayRange } from '../lib/history';
import { BatteryIndicator } from './DeviceBits';
import { DateRangePicker } from './DateRangePicker';
import { Button, ErrorText, Spinner } from './ui';

const PLAY_STEP_MS = 400;

function PointDetails({ point }: { point: Location }) {
  return (
    <div>
      <p className="text-lg font-semibold tabular-nums" data-testid="history-time">
        {formatDateTime(point.recordedAt)}
      </p>
      <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-600 dark:text-slate-400">
        <span>{formatAccuracy(point.accuracyM)}</span>
        {point.speed !== null && point.speed > 0.5 && <span>{formatSpeed(point.speed)}</span>}
        <span>{sourceLabel[point.source]}</span>
        <BatteryIndicator pct={point.batteryPct} charging={point.isCharging} />
      </p>
    </div>
  );
}

function Timeline({
  points,
  index,
  onIndex,
}: {
  points: Location[];
  index: number;
  onIndex: (index: number) => void;
}) {
  const [playing, setPlaying] = useState(false);
  const last = points.length - 1;
  const point = points[index];

  useEffect(() => {
    if (!playing) return;
    const timer = setTimeout(() => {
      if (index + 1 >= last) setPlaying(false);
      onIndex(Math.min(index + 1, last));
    }, PLAY_STEP_MS);
    return () => clearTimeout(timer);
  }, [playing, index, last, onIndex]);

  const play = () => {
    if (playing) return setPlaying(false);
    if (index >= last) onIndex(0);
    setPlaying(true);
  };

  if (!point) return null;
  return (
    <div className="space-y-3">
      <PointDetails point={point} />
      <div>
        <input
          type="range"
          min={0}
          max={last}
          value={index}
          onChange={(e) => {
            setPlaying(false);
            onIndex(Number(e.target.value));
          }}
          aria-label="Time along the route"
          aria-valuetext={formatDateTime(point.recordedAt)}
          className="w-full accent-blue-600"
          disabled={last === 0}
        />
        <div className="flex justify-between text-xs text-slate-500 tabular-nums">
          <span>{formatDateTime(points[0]?.recordedAt ?? 0)}</span>
          <span>{formatDateTime(points[last]?.recordedAt ?? 0)}</span>
        </div>
      </div>
      <div className="flex items-center justify-center gap-1">
        <Button
          variant="ghost"
          onClick={() => onIndex(Math.max(0, index - 1))}
          disabled={index === 0}
          aria-label="Previous location"
        >
          <SkipBack className="h-4 w-4" />
        </Button>
        <Button variant="secondary" onClick={play} disabled={last === 0} className="min-w-24">
          {playing ? <Pause className="h-4 w-4" aria-hidden /> : <Play className="h-4 w-4" aria-hidden />}
          {playing ? 'Pause' : 'Play'}
        </Button>
        <Button
          variant="ghost"
          onClick={() => onIndex(Math.min(last, index + 1))}
          disabled={index === last}
          aria-label="Next location"
        >
          <SkipForward className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

/**
 * The device panel's history view: pick a range of days, see how far the device went,
 * and scrub (or play) through where it was. The map draws the route alongside.
 */
export function HistoryPanel({
  device,
  now,
  range,
  onRangeChange,
  retentionDays,
  history,
  loading,
  error,
  points,
  index,
  onIndex,
  showApproximate,
  onShowApproximate,
  onBack,
}: {
  device: Device;
  now: number;
  range: DayRange;
  onRangeChange: (range: DayRange) => void;
  retentionDays: number | undefined;
  history: LocationHistoryResponse | undefined;
  loading: boolean;
  error: Error | null;
  /** The fixes on the timeline (approximate ones only when shown). */
  points: Location[];
  index: number;
  onIndex: (index: number) => void;
  showApproximate: boolean;
  onShowApproximate: (show: boolean) => void;
  onBack: () => void;
}) {
  const today = ukDate(now);
  const approximateCount = history?.locations.filter((l) => l.source === 'ip').length ?? 0;

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 border-b border-slate-200 px-3 py-2 dark:border-slate-800">
        <button
          type="button"
          onClick={onBack}
          className="rounded-lg p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800"
          aria-label={`Back to ${device.name}`}
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="min-w-0">
          <h2 className="font-semibold">History</h2>
          <p className="truncate text-xs text-slate-500">{device.name}</p>
        </div>
      </div>

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
        <div className="space-y-2">
          <div className="flex flex-wrap gap-1" role="group" aria-label="Quick ranges">
            {PRESETS.map((preset) => {
              const presetValue = presetRange(preset.id, now);
              const on = sameRange(presetValue, range);
              return (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => onRangeChange(presetValue)}
                  aria-pressed={on}
                  className={`rounded-full px-3 py-1 text-xs font-medium ${
                    on
                      ? 'bg-blue-600 text-white'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700'
                  }`}
                >
                  {preset.label}
                </button>
              );
            })}
          </div>
          <DateRangePicker
            value={range}
            onChange={onRangeChange}
            today={today}
            earliest={earliestDay(today, retentionDays)}
          />
        </div>

        {error && <ErrorText>{error.message}</ErrorText>}
        {loading && !history && <Spinner label="Loading history…" />}

        {history && (
          <>
            {history.locations.length === 0 ? (
              <div className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
                <p>
                  No locations were recorded{' '}
                  {compareDates(range.from, range.to) === 0
                    ? `on ${rangeLabel(range)}`
                    : `from ${formatCalendarDate(range.from)} to ${formatCalendarDate(range.to)}`}
                  .
                </p>
                {retentionDays !== undefined && (
                  <p>
                    History is kept for {retentionDays === 365 ? '1 year' : `${retentionDays} days`}. You can
                    change that in{' '}
                    <Link to="/settings" className="text-blue-700 hover:underline dark:text-sky-400">
                      Settings
                    </Link>
                    .
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-1 text-sm" data-testid="history-summary">
                <p>
                  <strong>
                    {history.distanceM < 20
                      ? 'No movement'
                      : `${formatDistance(history.distanceM)} travelled`}
                  </strong>
                  <span className="text-slate-500">
                    {' · '}
                    {points.length.toLocaleString('en-GB')} {points.length === 1 ? 'location' : 'locations'}
                  </span>
                </p>
                {history.simplified && (
                  <p className="text-xs text-slate-500">
                    Route simplified: showing {history.locations.length.toLocaleString('en-GB')} of{' '}
                    {history.total.toLocaleString('en-GB')} locations.
                  </p>
                )}
                {approximateCount > 0 && (
                  <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400">
                    <input
                      type="checkbox"
                      checked={showApproximate}
                      onChange={(e) => onShowApproximate(e.target.checked)}
                      className="accent-blue-600"
                    />
                    Show {approximateCount} approximate (IP address){' '}
                    {approximateCount === 1 ? 'location' : 'locations'}
                  </label>
                )}
              </div>
            )}
            {points.length > 0 && <Timeline points={points} index={index} onIndex={onIndex} />}
            {history.locations.length > 0 && points.length === 0 && (
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Only approximate locations were recorded in this range.
              </p>
            )}
          </>
        )}

        <div className="border-t border-slate-200 pt-3 text-xs text-slate-600 dark:border-slate-800 dark:text-slate-400">
          <p className="mb-1 flex items-center gap-1">
            <Download className="h-3.5 w-3.5" aria-hidden /> Download all of {device.name}’s history:
          </p>
          <a
            href={`${API_PREFIX}/export?format=gpx&device=${device.id}`}
            className="mr-3 text-blue-700 hover:underline dark:text-sky-400"
          >
            GPX (for maps and GPS apps)
          </a>
          <a
            href={`${API_PREFIX}/export?format=json&device=${device.id}`}
            className="text-blue-700 hover:underline dark:text-sky-400"
          >
            JSON
          </a>
        </div>
      </div>
    </div>
  );
}
